'use client';

import React from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {useTracker, TrackImpression} from '@udemy-v2/event-tracking';
import {OnePaneLayoutFullWidthSection} from '@udemy-v2/react-layout-components';
import {
    PersonalPlanBanner,
    SubscriptionCTAClickEvent,
    SubscriptionLearnMoreEvent,
    SubscriptionOfferPresented,
} from '@udemy-v2/react-subscription-browse';

import {useSubscriptionPlans} from '../../data/hooks/use-subscription-plans';

const AICareerBannerWrapper = ({children}: {children: React.ReactNode}) => (
    <OnePaneLayoutFullWidthSection>
        <OnePaneLayoutFullWidthSection.Content>{children}</OnePaneLayoutFullWidthSection.Content>
    </OnePaneLayoutFullWidthSection>
);

/**
 * AI Era Career Banner — delegates rendering to the shared PersonalPlanBanner component.
 * Uses Value Plan if available, else Personal Plan (matches v1 logic).
 * Value Plan gets custom title and description overrides; Personal Plan uses the shared defaults.
 * Regional-variant/dual-CTA logic lives entirely in PersonalPlanBanner, not here.
 *
 * Tracking events are constructed directly from @udemy-v2/react-subscription-browse classes,
 * matching v1. Those classes own the productType/planType <-> Avro schema mapping.
 */
export const AICareerBanner: React.FC = () => {
    const {plan, hasValuePlan, isLoading} = useSubscriptionPlans();
    const {gettext} = useI18n();
    const tracker = useTracker();

    if (isLoading) {
        return (
            <AICareerBannerWrapper>
                <PersonalPlanBanner isLoading />
            </AICareerBannerWrapper>
        );
    }

    if (!plan) {
        return null;
    }

    const opt = plan.annualPlanOption!;
    const ctaUrl = hasValuePlan ? '/pricing/' : '/personal-plan/';

    return (
        <AICareerBannerWrapper>
            <TrackImpression
                trackFunc={() =>
                    tracker.publishEvent(
                        new SubscriptionOfferPresented({
                            subscriptionPlan: plan,
                            productType: plan.productType,
                            countryCode: opt.monthlyPrice.currency ?? 'USD',
                            uiRegion: 'reimagine_career',
                        }),
                    )
                }
                onlyOnce
            >
                <PersonalPlanBanner
                    subscriptionPrice={parseFloat(opt.monthlyPrice.amount)}
                    ctaUrl={ctaUrl}
                    onCtaClick={() =>
                        tracker.publishEvent(
                            new SubscriptionCTAClickEvent({
                                planId: opt.id,
                                planType: plan.productType,
                                ctaType: opt.trial ? 'trial' : 'paid',
                                uiRegion: 'reimagine_career',
                                planPriceIds: [opt.id],
                            }),
                        )
                    }
                    onLearnMoreClick={() =>
                        tracker.publishEvent(
                            new SubscriptionLearnMoreEvent({
                                planId: opt.id,
                                planType: plan.productType,
                                uiRegion: 'reimagine_career',
                                planPriceIds: [opt.id],
                            }),
                        )
                    }
                    {...(hasValuePlan && {
                        title: gettext('Reimagine your career for the AI era'),
                        description: gettext(
                            'Future-proof your skills in the latest business and technology topics. Learn from real-world experts, right when you need it most.',
                        ),
                    })}
                />
            </TrackImpression>
        </AICareerBannerWrapper>
    );
};
